import React from "react";
import { Box, Button, Grid, Heading, HStack, Link, SimpleGrid, Text, VStack } from "@chakra-ui/react";
import { Link as RouterLink } from "react-router-dom";
import { BookOpen, Mail, MessageCircle, Rocket, Sparkles, Zap } from "lucide-react";

const products = [
  {
    title: "Smart Schedule Finder",
    subtitle: "Find the best open slot in seconds.",
    description:
      "Build same-day booking requests and get ranked recommendations based on travel, timing, and schedule fit. Review route details before you confirm the booking.",
    cta: "Open Smart Schedule Finder",
    to: "/schedule",
    icon: Zap,
    accent: "blue",
  },
  {
    title: "Waitlist Autopilot",
    subtitle: "Fill openings automatically with waitlist texting.",
    description:
      "Keep your schedule full with less manual work. Fill last-minute openings faster by automatically inviting clients to join a waitlist and then texting them when appointments open up.",
    cta: "Open Waitlist Autopilot",
    to: "/waitlist",
    icon: Sparkles,
    accent: "green",
  },
  {
    title: "Fast Book Banner",
    subtitle: "Turn last-minute availability into new bookings.",
    description:
      "Configure your embeddable banner, choose CTA behavior, and match your brand colors. Monitor views, clicks, and click-through performance while promoting your next opening.",
    cta: "Open Fast Book Banner",
    to: "/embed",
    icon: Rocket,
    accent: "orange",
  },
];

export default function Dashboard() {
  return (
    <Box maxW="1800px" mx="auto" px={{ base: 4, md: 6 }} py={{ base: 6, md: 8 }}>
      <VStack align="stretch" spacing={6}>
        <Box>
          <Heading size="lg">Revenue Engines</Heading>
          <Text mt={1} color="gray.600" fontSize="sm">
            Choose a workflow below to optimize scheduling, fill openings faster, and convert demand.
          </Text>
        </Box>

        <Grid templateColumns={{ base: "1fr", lg: "repeat(3, 1fr)" }} gap={6}>
          {products.map((product) => {
            const Icon = product.icon;
            return (
              <Box
                key={product.title}
                border="1px solid"
                borderColor="gray.200"
                borderRadius="lg"
                p={6}
                bg="white"
              >
                <HStack spacing={3} mb={3}>
                  <Box
                    display="inline-flex"
                    alignItems="center"
                    justifyContent="center"
                    p={2}
                    borderRadius="md"
                    bg={`${product.accent}.50`}
                    color={`${product.accent}.600`}
                  >
                    <Icon size={18} />
                  </Box>
                  <Heading size="sm">{product.title}</Heading>
                </HStack>

                <Text fontSize="sm" fontWeight="600" color="gray.700" mb={2}>
                  {product.subtitle}
                </Text>
                <Text fontSize="sm" color="gray.600" mb={5}>
                  {product.description}
                </Text>

                <Button
                  as={RouterLink}
                  to={product.to}
                  colorScheme={product.accent}
                  size="sm"
                >
                  {product.cta}
                </Button>
              </Box>
            );
          })}
        </Grid>

        <Box pt={2}>
          <Heading size="md">Contact Support</Heading>
          <Text mt={1} color="gray.600" fontSize="sm">
            Need help with Same Day Openings? You can read the support documentation, reach support by email, or use the live chat feature in the app.
          </Text>

          <SimpleGrid columns={{ base: 1, md: 3 }} spacing={4} mt={4}>
            <Box bg="white" border="1px solid" borderColor="gray.200" borderRadius="lg" p={5}>
              <HStack align="start" spacing={3}>
                <Box color="blue.500" pt={1}>
                  <BookOpen size={22} />
                </Box>
                <Box>
                  <Heading size="sm">Support documentation</Heading>
                  <Text mt={2} color="gray.600" fontSize="sm">
                    Read guides and troubleshooting articles for Same Day Openings.
                  </Text>
                  <Button
                    as="a"
                    href="http://support.samedayopenings.com/"
                    target="_blank"
                    rel="noopener noreferrer"
                    mt={4}
                    colorScheme="blue"
                    size="sm"
                    leftIcon={<BookOpen size={16} />}
                  >
                    Read Docs
                  </Button>
                </Box>
              </HStack>
            </Box>

            <Box bg="white" border="1px solid" borderColor="gray.200" borderRadius="lg" p={5}>
              <HStack align="start" spacing={3}>
                <Box color="blue.500" pt={1}>
                  <Mail size={22} />
                </Box>
                <Box>
                  <Heading size="sm">Email support</Heading>
                  <Text mt={2} color="gray.600" fontSize="sm">
                    Send a message to{" "}
                    <Link href="mailto:support@samedayopenings.com" color="blue.600" fontWeight="semibold">
                      support@samedayopenings.com
                    </Link>
                    .
                  </Text>
                  <Button
                    as="a"
                    href="mailto:support@samedayopenings.com"
                    mt={4}
                    colorScheme="blue"
                    size="sm"
                    leftIcon={<Mail size={16} />}
                  >
                    Email Support
                  </Button>
                </Box>
              </HStack>
            </Box>

            <Box bg="white" border="1px solid" borderColor="gray.200" borderRadius="lg" p={5}>
              <HStack align="start" spacing={3}>
                <Box color="blue.500" pt={1}>
                  <MessageCircle size={22} />
                </Box>
                <Box>
                  <Heading size="sm">Live chat</Heading>
                  <Text mt={2} color="gray.600" fontSize="sm">
                    Use the live chat widget in the app when you want to contact the support team from your account.
                  </Text>
                </Box>
              </HStack>
            </Box>
          </SimpleGrid>
        </Box>
      </VStack>
    </Box>
  );
}
